import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Briefcase, Building2, Wallet, Heart, Activity, Compass, Clock, Sparkles, Lock, Zap, Brain, LogIn, LogOut, User, ChevronDown, CheckCircle, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getSubscriptionTier, initializeUser, isPaidUser, getAuthUser, isLoggedIn, loginWithGoogle, logout, verifySession } from "@/lib/subscription";

const fadeIn = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } }
};

const domains = [
  { id: "career", icon: Briefcase, title: "Career & Education", desc: "Jobs, promotions, pivots" },
  { id: "business", icon: Building2, title: "Business & Startup", desc: "Strategy, investments" },
  { id: "finance", icon: Wallet, title: "Money & Finance", desc: "Spending, saving, investing" },
  { id: "relationships", icon: Heart, title: "Relationships", desc: "Commitments, boundaries" },
  { id: "health", icon: Activity, title: "Personal Well-Being", desc: "Habits, changes, choices" },
  { id: "ethics", icon: Compass, title: "Ethics & Life", desc: "Values, long-term paths" },
];

const MODAL_PRICING = {
  decision_partner: { INR: "₹99/mo", USD: "$3/mo" },
  enterprise: { INR: "₹999/mo", USD: "$12/mo" }
};

const detectCurrency = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = navigator.language || "";
    if (tz.startsWith("Asia/Kolkata") || tz.startsWith("Asia/Calcutta") || lang.startsWith("hi") || lang === "en-IN") {
      return "INR";
    }
  } catch (e) { /* timezone detection fallback */ }
  return "USD";
};

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDomain, setSelectedDomain] = useState("career");
  const [history, setHistory] = useState([]);
  const [showFounder, setShowFounder] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [modalCurrency, setModalCurrency] = useState(() => detectCurrency());
  const userMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Check if user data was passed from auth callback
    if (location.state?.user) {
      setAuthUser(location.state.user);
      return; // Don't verify - just came from auth callback
    }
    
    // Check for existing logged-in user in localStorage
    const storedUser = getAuthUser();
    if (storedUser) {
      // Trust localStorage - it was set during successful auth
      setAuthUser(storedUser);
    } else {
      // No localStorage data - try to verify session from cookie
      verifySession().then(verifiedUser => {
        if (verifiedUser) {
          setAuthUser(verifiedUser);
        }
      });
    }
    
    // Load decision history from localStorage
    const saved = localStorage.getItem("decisora_history");
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load history");
      }
    }
    
    // Initialize user and fetch status
    initializeUser().then(status => {
      setUserStatus(status);
    });
  }, [location.state]);

  const handleStartDecision = () => {
    navigate(`/decide?domain=${selectedDomain}`);
  };

  const tier = userStatus?.tier || userStatus?.plan || getSubscriptionTier();
  const paid = tier === 'decision_partner' || tier === 'enterprise';
  const isPremiumActive = authUser?.is_premium || false;
  const isEnterprise = (authUser?.plan === 'enterprise' || tier === 'enterprise') && isPremiumActive;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <span className="gradient-text font-heading font-semibold text-xs md:text-sm">D</span>
            </div>
            <span className="font-heading text-base md:text-lg font-medium text-foreground">
              DECISORA
            </span>
          </div>
          <div className="flex items-center gap-1 md:gap-3">
            {/* ⚡ Plans button - ALWAYS visible for all users */}
            <Button
              data-testid="plans-btn"
              variant="ghost"
              size="sm"
              onClick={() => setShowPlansModal(true)}
              className="text-xs text-muted-foreground hover:text-foreground h-7 md:h-9 px-1.5 md:px-3"
            >
              <Sparkles className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
              <span className="hidden md:inline">Plans</span>
            </Button>
            {/* History - always visible */}
            <Button
              data-testid="history-btn"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (!authUser) {
                  toast.info("Sign in to view your decision history");
                  return;
                }
                navigate("/history");
              }}
              className="text-xs md:text-sm text-muted-foreground hover:text-foreground h-7 md:h-9 px-1.5 md:px-3 hidden md:inline-flex"
            >
              <Clock className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
              History
            </Button>
            {/* Start deciding */}
            <Button
              data-testid="header-start-btn"
              variant="ghost"
              size="sm"
              onClick={handleStartDecision}
              className="text-xs text-muted-foreground hover:text-foreground h-7 md:h-9 px-1.5 md:px-3"
            >
              Start
            </Button>
            {/* Login/Logout */}
            {authUser ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-1 md:gap-1.5 px-1.5 md:px-2 py-1 md:py-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
                >
                  {authUser.picture ? (
                    <img 
                      src={authUser.picture} 
                      alt={authUser.name} 
                      className="w-6 h-6 md:w-7 md:h-7 rounded-full border border-border"
                    />
                  ) : (
                    <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">
                      {(authUser.name || authUser.email || "U").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <ChevronDown className="h-3 w-3 md:h-3.5 md:w-3.5 text-muted-foreground" />
                </button>
                
                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-3 py-2 border-b border-border">
                      <p className="text-sm font-medium text-foreground truncate">{authUser.name || "User"}</p>
                      <p className="text-xs text-muted-foreground truncate">{authUser.email}</p>
                    </div>
                    <button
                      data-testid="menu-history-btn"
                      onClick={() => { setShowUserMenu(false); navigate("/history"); }}
                      className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/50 flex items-center gap-2"
                    >
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      History
                    </button>
                    <button
                      data-testid="menu-logout-btn"
                      onClick={() => { setShowUserMenu(false); logout(); }}
                      className="w-full px-3 py-2 text-left text-sm text-foreground hover:bg-secondary/50 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4 text-muted-foreground" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                data-testid="login-btn"
                variant="outline"
                size="sm"
                onClick={loginWithGoogle}
                className="text-xs md:text-sm h-8 md:h-9 px-2 md:px-3"
              >
                <LogIn className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="initial"
            animate="animate"
            variants={stagger}
            className="max-w-2xl"
          >
            <motion.div variants={fadeIn} className="mb-8">
              <span className="inline-block px-3 py-1.5 rounded-full text-xs font-medium pillar-active">
                Decision Intelligence
              </span>
            </motion.div>
            
            <motion.h1
              variants={fadeIn}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-foreground leading-[1.1] mb-6"
            >
              Clarity for the decisions
              <br />
              <span className="gradient-text">that shape your life.</span>
            </motion.h1>
            
            <motion.p
              variants={fadeIn}
              className="text-lg text-muted-foreground leading-relaxed mb-4 max-w-lg"
            >
              Structured reasoning frameworks that surface your own thinking.
              No chatbots. No generic advice. Just clarity.
            </motion.p>
            
            <motion.p
              variants={fadeIn}
              className="text-sm text-muted-foreground/70 italic mb-10 max-w-lg"
            >
              "Decisora doesn't just answer — it challenges your thinking to help you avoid costly mistakes."
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Domain Selector */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Decision Domain
            </p>
            <h2 className="font-heading text-2xl text-foreground">
              What type of decision?
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {domains.map((domain, i) => (
              <motion.button
                key={domain.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                onClick={() => setSelectedDomain(domain.id)}
                className={`p-4 rounded-xl text-left transition-all ${
                  selectedDomain === domain.id 
                    ? 'glow-card' 
                    : 'floating-card hover:border-border'
                }`}
                data-testid={`domain-${domain.id}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${
                  selectedDomain === domain.id ? 'bg-primary/20' : 'bg-secondary'
                }`}>
                  <domain.icon className={`h-4 w-4 ${
                    selectedDomain === domain.id ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                </div>
                
                <h3 className={`font-medium text-sm mb-1 ${
                  selectedDomain === domain.id ? 'text-foreground' : 'text-foreground/80'
                }`}>
                  {domain.title}
                </h3>
                
                <p className="text-xs text-muted-foreground">
                  {domain.desc}
                </p>
              </motion.button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              data-testid="hero-start-btn"
              onClick={handleStartDecision}
              className="h-12 px-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium shadow-lg shadow-primary/20"
            >
              Begin a {domains.find(d => d.id === selectedDomain)?.title.toLowerCase()} decision
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            {isEnterprise && (
              <Button
                data-testid="enterprise-start-btn"
                onClick={() => navigate("/enterprise")}
                variant="outline"
                className="h-12 px-6 rounded-full text-sm font-medium border-primary/50 hover:bg-primary/10"
              >
                <Building2 className="mr-2 h-4 w-4" />
                Enterprise Decision
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Frameworks Preview */}
      <section className="py-12 px-6 border-t border-border/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
              8 Frameworks
            </p>
            <h2 className="font-heading text-2xl text-foreground">
              Structured thinking, not guesswork
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-3">
            {[
              { name: "10-10-10 Rule", desc: "Time horizons" },
              { name: "Inversion", desc: "Failure prevention" },
              { name: "Regret Min", desc: "Future self" },
              { name: "Opportunity Cost", desc: "Trade-offs" },
              { name: "Risk vs Reversibility", desc: "Downside analysis" },
              { name: "First Principles", desc: "Core truths" },
              { name: "Second-Order", desc: "Downstream effects" },
              { name: "Worst/Best Case", desc: "Extreme mapping" }
            ].map((fw, i) => (
              <motion.div
                key={fw.name}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="p-4 rounded-lg bg-secondary/30 border border-border/40"
              >
                <p className="text-sm font-medium text-foreground mb-1">{fw.name}</p>
                <p className="text-xs text-muted-foreground">{fw.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pro Features Preview */}
      <section className="py-12 px-6 border-t border-border/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <p className="text-xs font-medium text-primary uppercase tracking-wider mb-3">
              Advanced Tools
            </p>
            <h2 className="font-heading text-2xl text-foreground">
              Go deeper with your decisions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* Challenge My Choice */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`relative p-5 rounded-xl border ${paid ? 'floating-card' : 'bg-secondary/20 border-border/40'}`}
            >
              {!paid && (
                <div className="absolute top-4 right-4 group">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <div className="absolute right-0 top-6 opacity-0 group-hover:opacity-100 transition-opacity bg-popover border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground whitespace-nowrap z-10 shadow-lg">
                    Available after free trial / upgrade
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${paid ? 'bg-primary/20' : 'bg-secondary/50'}`}>
                  <Zap className={`h-5 w-5 ${paid ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">Challenge My Choice</h3>
                  <p className="text-xs text-muted-foreground">Devil's Advocate mode</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Stress-test your decision with counter-arguments, blind spots, and risks you might have missed.
              </p>
              {!paid && (
                <button 
                  onClick={() => navigate('/pricing')}
                  className="mt-4 text-xs text-primary hover:underline"
                >
                  Unlock with Decision Partner →
                </button>
              )}
            </motion.div>

            {/* Deep Reasoning */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className={`relative p-5 rounded-xl border ${paid ? 'floating-card' : 'bg-secondary/20 border-border/40'}`}
            >
              {!paid && (
                <div className="absolute top-4 right-4 group">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <div className="absolute right-0 top-6 opacity-0 group-hover:opacity-100 transition-opacity bg-popover border border-border rounded-lg px-3 py-2 text-xs text-muted-foreground whitespace-nowrap z-10 shadow-lg">
                    Available after free trial / upgrade
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${paid ? 'bg-primary/20' : 'bg-secondary/50'}`}>
                  <Brain className={`h-5 w-5 ${paid ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">Deep Reasoning</h3>
                  <p className="text-xs text-muted-foreground">Extended analysis</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Get deeper trade-off analysis, future implications, and longer-form reasoning for complex decisions.
              </p>
              {!paid && (
                <button 
                  onClick={() => navigate('/pricing')}
                  className="mt-4 text-xs text-primary hover:underline"
                >
                  Unlock with Decision Partner →
                </button>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-muted-foreground">
            {["State decision", "Select analysis method", "Think step by step", "Get clarity"].map((step, i) => (
              <div key={step} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-secondary text-foreground text-xs font-medium flex items-center justify-center">
                  {i + 1}
                </span>
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise API Integration Section */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto text-center space-y-5">
          <div className="h-px w-16 bg-primary/40 mx-auto" />
          <h2 className="font-heading text-lg md:text-xl text-foreground">
            Enterprise API Integration
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
            Integrate Decisora&apos;s Decision Engine directly into your company&apos;s applications, workflows, dashboards, or internal systems through secure enterprise APIs.
          </p>
          <Button
            onClick={() => navigate("/contact")}
            className="h-10 px-6 rounded-full text-sm"
            data-testid="homepage-api-access-btn"
          >
            Request API Access
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 border-t border-border/30">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-md bg-primary/20 flex items-center justify-center">
              <span className="gradient-text font-heading font-semibold text-xs">D</span>
            </div>
            <span className="text-sm text-muted-foreground">DECISORA</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFounder(!showFounder)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Founder
            </button>
            <p className="text-xs text-muted-foreground">
              Clarity &gt; Comfort
            </p>
          </div>
        </div>
        
        <AnimatePresence>
          {showFounder && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="max-w-5xl mx-auto mt-6 pt-6 border-t border-border/30"
            >
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
                Founder
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl">
                Decisora is built by Harsha Sai Akhila, an 18-year-old solo founder and B.Tech 1st year student.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xl mt-3">
                The project started as a personal attempt to slow down emotional decisions using structured thinking frameworks, and is being built in public while learning from real usage and feedback.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </footer>

      {/* Policy Links Footer */}
      <div className="border-t border-border/30 py-6 px-4 md:px-6">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-3">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <a href="/terms" className="hover:text-foreground transition-colors">Terms</a>
            <span className="text-border">|</span>
            <a href="/privacy" className="hover:text-foreground transition-colors">Privacy</a>
            <span className="text-border">|</span>
            <a href="/refund" className="hover:text-foreground transition-colors">Refund</a>
            <span className="text-border">|</span>
            <a href="/contact" className="hover:text-foreground transition-colors">Contact</a>
          </div>
          <p className="text-xs text-muted-foreground/60 flex items-center gap-1">
            <span>🔒</span> Secure payments powered by Razorpay
          </p>
        </div>
      </div>

      {/* Plans Modal - Always accessible via ⚡ icon */}
      <Dialog open={showPlansModal} onOpenChange={setShowPlansModal}>
        <DialogContent className="sm:max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Your Plan
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Currency Toggle */}
            <div className="flex items-center justify-center gap-2" data-testid="modal-currency-toggle">
              <Globe className="h-3 w-3 text-muted-foreground" />
              <div className="inline-flex items-center rounded-full border border-border bg-secondary/30 p-0.5">
                <button
                  onClick={() => setModalCurrency("INR")}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-all ${
                    modalCurrency === "INR"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="modal-currency-inr"
                >
                  ₹ INR
                </button>
                <button
                  onClick={() => setModalCurrency("USD")}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-all ${
                    modalCurrency === "USD"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid="modal-currency-usd"
                >
                  $ USD
                </button>
              </div>
            </div>

            {/* Decision Partner Plan */}
            <div className={`p-4 rounded-lg border ${
              (authUser && isPremiumActive && !isEnterprise)
                ? "border-primary bg-primary/5" 
                : "border-border"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">Decision Partner</span>
                  {(authUser && isPremiumActive && !isEnterprise) && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Active
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-foreground">{MODAL_PRICING.decision_partner[modalCurrency]}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Challenge My Choice + Deep Reasoning • 2 decisions/week
              </p>
              {(authUser && isPremiumActive && !isEnterprise) ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs bg-primary/10 border-primary cursor-default"
                  disabled
                  data-testid="plan-partner-current"
                >
                  <CheckCircle className="h-3 w-3 mr-1.5" />
                  Current Plan
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="w-full h-8 text-xs"
                  onClick={() => {
                    setShowPlansModal(false);
                    navigate("/pricing");
                  }}
                  data-testid="plan-partner-upgrade"
                >
                  Upgrade
                </Button>
              )}
            </div>

            {/* Enterprise Plan */}
            <div className={`p-4 rounded-lg border ${
              (authUser && isEnterprise) ? "border-primary bg-primary/5" : "border-border"
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">Enterprise</span>
                  {(authUser && isEnterprise) && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary/20 text-primary flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Active
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold text-foreground">{MODAL_PRICING.enterprise[modalCurrency]}</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Custom Decision Modeling + Priority Execution • Strategic analysis
              </p>
              {(authUser && isEnterprise) ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-8 text-xs bg-primary/10 border-primary cursor-default"
                  disabled
                  data-testid="plan-enterprise-current"
                >
                  <CheckCircle className="h-3 w-3 mr-1.5" />
                  Current Plan
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant={(authUser && isPremiumActive) ? "default" : "outline"}
                  className="w-full h-8 text-xs"
                  onClick={() => {
                    setShowPlansModal(false);
                    navigate("/pricing");
                  }}
                  data-testid="plan-enterprise-upgrade"
                >
                  Upgrade
                </Button>
              )}
            </div>

            {/* Free tier info */}
            {!(authUser && isPremiumActive) && (
              <p className="text-xs text-muted-foreground text-center">
                Free: 3 decisions/day • 1 pro feature trial
              </p>
            )}

            <Button
              variant="outline"
              className="w-full h-9"
              onClick={() => setShowPlansModal(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
